// Libraries
import { useEffect, useState } from 'react';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

// Common
import { ADialog, ADialogActions, ADialogContent, ADialogTitle } from '../common/dialog';

// Core
import { ResourceTextApplication, SystemText, systemTextApi } from '../core/resources';
import FormValidator from '../core/FormValidator';

// Feature
import { IAccessRole } from './IAccessRightsManagement';
import accessManagementApi from '../api/accessManagementApi';
import RoleForm from './RoleForm';
import { CompanyEntityTypeEnum } from '../interfaces/enums';

export interface RoleDialogProps {
    afterSaveFail: () => any;
    afterSaveSuccess: (mode: 'add' | 'edit', role: IAccessRole) => any;
    data: IAccessRole | null;
    onClose: () => any;
}

export default function RoleDialog(props: RoleDialogProps) {
    const { t } = useTranslation<ResourceTextApplication[]>([
        'AnelmaAccessRights',
        'AnelmaGeneral',
    ]);

    const rolePrefix = 'Snellman Anelma - ';
    const addDialog = props.data === null;
    const dialogTitle = addDialog ? 1003 : 1010;
    const validator = new FormValidator();
    const { enqueueSnackbar } = useSnackbar();

    const [isLoading, setIsLoading] = useState<boolean>(!addDialog);
    const [roleTexts, setRoleTexts] = useState<SystemText>({
        additionalInfo: 'Snellman Anelma access role',
        application: 'AnelmaAccessRights',
        code: null,
        cultures: [
            { culture: 'fi', value: '' },
            { culture: 'sv-SE', value: '' },
            { culture: 'en-GB', value: '' },
        ],
    });
    const [roleGroup, setRoleGroup] = useState<CompanyEntityTypeEnum>(CompanyEntityTypeEnum.Farm);
    const [originalRoleGroup, setOriginalroleGroup] = useState<CompanyEntityTypeEnum>(
        CompanyEntityTypeEnum.Farm
    );

    function onRoleSave() {
        if (validator.invalid) {
            enqueueSnackbar(t('AnelmaGeneral:1030'), {
                variant: 'error',
            });
            return;
        }

        setIsLoading(true);

        const onFail = () => {
            setIsLoading(false);
            props.afterSaveFail();
        };

        systemTextApi.save(roleTexts).then(
            (textResponse) => {
                i18next.reloadResources(null, ['AnelmaAccessRights']).then(
                    () => {
                        if (addDialog) {
                            const descPrefix = rolePrefix;
                            const namePrefix = rolePrefix;
                            const role2: IAccessRole = {
                                description: `${descPrefix}${textResponse.cultures.find((i) => i.culture === 'fi')?.value
                                    }`,
                                id: props.data?.id || null,
                                name: `${namePrefix}${textResponse.cultures.find((i) => i.culture === 'en-GB')?.value
                                    }`,
                                textCode: textResponse.code as number,
                            };

                            accessManagementApi
                                .createNewRole(role2)
                                .then(
                                    (roleResponse) => {
                                        if (!roleResponse) return;

                                        const savedRole = roleResponse.Entity;
                                        accessManagementApi.attachRole(roleGroup, role2)
                                            .then(
                                                () => {
                                                    props.afterSaveSuccess('add', savedRole);
                                                    props.onClose();
                                                },
                                                () => onFail()
                                            );
                                    },
                                    () => onFail()
                                );
                        }
                        else {
                            if (props.data) {
                                const role2: IAccessRole = {
                                    description: props.data.description,
                                    id: props.data.id,
                                    name: props.data.name,
                                    textCode: textResponse.code as number,
                                };

                                if (roleGroup !== originalRoleGroup) {
                                    accessManagementApi.detachRole(
                                        originalRoleGroup,
                                        role2
                                    ).then(
                                        () => {
                                            accessManagementApi.attachRole(roleGroup, role2)
                                                .then(
                                                    () => {
                                                        props.afterSaveSuccess('edit', role2);
                                                        props.onClose();
                                                    },
                                                    () => onFail());
                                        },
                                        () => onFail());
                                }
                                else {
                                    props.afterSaveSuccess('edit', role2);
                                    props.onClose();
                                }
                            }

                        }
                    },
                    () => onFail()
                );
            },
            () => onFail()
        );
    }

    useEffect(() => {
        if (!addDialog) {
            const promise = systemTextApi
                .get('AnelmaAccessRights', props.data?.textCode as number)
                .then((data) => {
                    setRoleTexts(data);
                });
            const promise2 = accessManagementApi
                .getRoleGroup(props.data?.name as string)
                .then((data) => {
                    setOriginalroleGroup(data);
                    setRoleGroup(data);
                });

            Promise.all([promise, promise2]).then(() => {
                setIsLoading(false);
            });
        }
    }, [addDialog, props]);

    return (
        <>
            {
                <ADialog onClose={() => props.onClose()} open={true}>
                    <ADialogTitle>{t(`AnelmaAccessRights:${dialogTitle}`)}</ADialogTitle>

                    <ADialogContent isLoading={isLoading} size='sm'>
                        <RoleForm
                            data={roleTexts}
                            name={props.data?.name ?? ''}
                            RoleGroup={roleGroup}
                            onChange={(v) => setRoleTexts(v)}
                            onRoleGroupChange={(v) => setRoleGroup(v)}
                            validator={validator}
                        />
                    </ADialogContent>

                    <ADialogActions
                        buttons={{
                            left: [
                                {
                                    onClick: () => props.onClose(),
                                    type: 'cancel',
                                },
                            ],
                            right: [
                                {
                                    onClick: () => onRoleSave(),
                                    type: 'save',
                                },
                            ],
                        }}
                        isLoading={isLoading}
                    />
                </ADialog>
            }
        </>
    );
}
